import type { PositionFilter } from '@on3/api';
import { TextLink } from '@public/components/UI/Link/TextLink';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

import styles from './DraftByCategoryNavigation.module.scss';

const draftCategories = {
  'draft-by-year': 'year',
  'draft-by-position': 'position',
  'draft-by-state': 'state',
  'draft-by-rating': 'star rating',
  'top-draft-picks': 'top draft picks',
} as const;

interface IDraftByCategoryNavigation {
  positions?: PositionFilter[];
}
export const DraftByCategoryNavigation = ({
  positions,
}: IDraftByCategoryNavigation) => {
  const router = useRouter();
  const { category, year, 'team-key': teamKey, position } = router.query;
  const isByYear = router.asPath.includes('draft-by-year');
  const isByPosition = category === 'draft-by-position';
  const isNBA = router.asPath.includes('nba');
  const league = isNBA ? 'nba' : 'nfl';
  const isActive = useCallback(
    (param: string) => {
      if (param === 'draft-by-year' && isByYear) {
        return true;
      }

      if (param === category) {
        return true;
      }

      return false;
    },
    [category, isByYear],
  );

  const getHref = (cat: keyof typeof draftCategories) => {
    return `/${league}/draft/${year}/draft-history/${cat}/?team-key=${teamKey}`;
  };
  const getPositionHref = (key: string | number) => {
    return {
      pathname: router.pathname,
      query: { ...router.query, position: key },
    };
  };

  return (
    <>
      <ul className={styles.navigationList}>
        <li className={styles.navigationItem}>
          <TextLink
            className={clsx(styles.navigationLink, {
              [styles.active]: isActive('draft-by-year'),
            })}
            href={getHref('draft-by-year')}
            underline="none"
            variant="subtitle1"
          >
            by year
          </TextLink>
        </li>
        <li className={styles.navigationItem}>
          <TextLink
            className={clsx(styles.navigationLink, {
              [styles.active]: isActive('top-draft-picks'),
            })}
            href={getHref('top-draft-picks')}
            underline="none"
            variant="subtitle1"
          >
            top draft picks
          </TextLink>
        </li>
        <li className={styles.navigationItem}>
          <TextLink
            className={clsx(styles.navigationLink, {
              [styles.active]: isActive('draft-by-position'),
            })}
            href={getHref('draft-by-position')}
            underline="none"
            variant="subtitle1"
          >
            by position
          </TextLink>
        </li>
        <li className={styles.navigationItem}>
          <TextLink
            className={clsx(styles.navigationLink, {
              [styles.active]: isActive('draft-by-state'),
            })}
            href={getHref('draft-by-state')}
            underline="none"
            variant="subtitle1"
          >
            by state
          </TextLink>
        </li>
        <li className={styles.navigationItem}>
          <TextLink
            className={clsx(styles.navigationLink, {
              [styles.active]: isActive('draft-by-rating'),
            })}
            href={getHref('draft-by-rating')}
            underline="none"
            variant="subtitle1"
          >
            by hs star rating
          </TextLink>
        </li>
      </ul>
      {isByPosition && positions && (
        <ul className={styles.positionsList}>
          {positions.map((pos) => {
            return (
              <li className={styles.positionItem} key={pos.key}>
                <TextLink
                  className={clsx(styles.positionLink, {
                    [styles.active]: pos.key?.toString() === position,
                  })}
                  href={getPositionHref(`${pos.key}`)}
                  underline="none"
                  variant="subtitle1"
                >
                  {pos.abbreviation}
                </TextLink>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
};
